import React, { useMemo, useRef, useState } from 'react';
import {
  HeartPulse, TrendingDown, TrendingUp, UserX, UserPlus, Minus, Download, Search, X, Info
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAccountHealthAccounts } from '../../hooks/useAdminDashboard';
import { RetentionSection, ActivationSection, RewardsSection } from './AccountHealthTrends';
import { fmtMoney, fmtPct, downloadCsv } from './accountHealthFormat';

// Admin "Account Health" tab. Top half answers "which accounts are slipping and
// whose book are they in"; the sections below track revenue retention, signup
// activation and volume-rewards reach over time. All money is paid invoices by
// payment date (same basis as Overview); AM is the account's CURRENT assignment.

type Mode = 'rolling' | 'calendar';
type Status = 'churned' | 'slipping' | 'stable' | 'growing' | 'new' | 'reactivated';
type SortKey = 'change' | 'baselineMonthly' | 'current' | 'daysSinceLastPaid' | 'lifetimeRevenue';

interface Account {
  physicianId: string;
  name: string;
  clinicName: string;
  email: string;
  amId: string | null;
  amName: string | null;
  baselineMonthly: number;
  current: number;
  change: number;
  changePct: number | null;
  status: Status;
  lastPaidAt: string | null;
  daysSinceLastPaid: number | null;
  lifetimeRevenue: number;
  sparkline: { month: string; revenue: number }[];
}

interface AmRow {
  amId: string | null;
  amName: string;
  accounts: number;
  baselineMonthly: number;
  current: number;
  change: number;
  slippingAccounts: number;
  churnedAccounts: number;
  lostRevenue: number;
  gainedRevenue: number;
}

const UNASSIGNED = 'unassigned';
const PAGE_SIZE = 25;

const STATUS_META: Record<Status, { label: string; icon: React.ElementType; className: string }> = {
  churned: { label: 'Churned', icon: UserX, className: 'bg-red-50 text-red-700 border-red-200' },
  slipping: { label: 'Slipping', icon: TrendingDown, className: 'bg-orange-50 text-orange-700 border-orange-200' },
  stable: { label: 'Stable', icon: Minus, className: 'bg-gray-50 text-gray-600 border-gray-200' },
  growing: { label: 'Growing', icon: TrendingUp, className: 'bg-green-50 text-green-700 border-green-200' },
  new: { label: 'New', icon: UserPlus, className: 'bg-blue-50 text-blue-700 border-blue-200' },
  reactivated: { label: 'Reactivated', icon: UserPlus, className: 'bg-blue-50 text-blue-700 border-blue-200' }
};

const STATUS_ORDER: Status[] = ['churned', 'slipping', 'stable', 'growing', 'new', 'reactivated'];

// Last 12 complete calendar months, newest first, as YYYY-MM.
const completeMonths = (): { value: string; label: string }[] => {
  const now = new Date();
  const out = [];
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    });
  }
  return out;
};

export const StatusPill: React.FC<{ status: Status }> = ({ status }) => {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${meta.className}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

// Six-month revenue trend. Hover a point for the month and amount.
const Sparkline: React.FC<{ points: { month: string; revenue: number }[] }> = ({ points }) => {
  const w = 96;
  const h = 28;
  const pad = 4;
  const max = Math.max(...points.map(p => p.revenue), 1);
  const step = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const xy = points.map((p, i) => [pad + i * step, h - pad - (p.revenue / max) * (h - pad * 2)] as const);
  const monthLabel = (m: string) => {
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, mo - 1, 1).toLocaleDateString('en-US', { month: 'short' });
  };
  return (
    <svg width={w} height={h} role="img" aria-label={points.map(p => `${monthLabel(p.month)} ${fmtMoney(p.revenue)}`).join(', ')}>
      <line x1={pad} x2={w - pad} y1={h - pad} y2={h - pad} className="stroke-gray-200" strokeWidth={1} />
      <polyline
        points={xy.map(([x, y]) => `${x},${y}`).join(' ')}
        fill="none"
        className="stroke-blue-500"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {xy.map(([x, y], i) => (
        <g key={points[i].month}>
          <circle cx={x} cy={y} r={i === xy.length - 1 ? 2.5 : 0} className="fill-blue-500" />
          {/* Hit target wider than the mark */}
          <rect x={x - step / 2} y={0} width={Math.max(step, 8)} height={h} fill="transparent">
            <title>{`${monthLabel(points[i].month)}: ${fmtMoney(points[i].revenue)}`}</title>
          </rect>
        </g>
      ))}
    </svg>
  );
};

const StatTile: React.FC<{
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  active?: boolean;
  onClick?: () => void;
}> = ({ label, value, sub, icon: Icon, active, onClick }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      className={`text-left bg-white shadow-sm rounded-2xl border p-4 transition-colors ${
        active ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'
      } ${onClick ? 'hover:border-gray-300 cursor-pointer' : ''}`}
    >
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-500">{sub}</div>}
    </Tag>
  );
};

const AccountHealthPanel: React.FC = () => {
  const months = useMemo(completeMonths, []);
  const [mode, setMode] = useState<Mode>('rolling');
  const [month, setMonth] = useState(months[0].value);
  const [threshold, setThreshold] = useState(30);
  const [statusFilter, setStatusFilter] = useState<Set<Status>>(new Set(['churned', 'slipping']));
  const [amFilter, setAmFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('change');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [showAllAms, setShowAllAms] = useState(false);
  const accountsRef = useRef<HTMLDivElement>(null);

  const query = useAccountHealthAccounts({
    mode,
    month: mode === 'calendar' ? month : undefined,
    threshold
  });
  const data = query.data;
  const accounts: Account[] = data?.accounts || [];
  const amRollup: AmRow[] = data?.amRollup || [];

  const amOptions = useMemo(
    () => amRollup
      .map(r => ({ value: r.amId || UNASSIGNED, label: r.amName }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [amRollup]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = accounts.filter(a =>
      statusFilter.has(a.status) &&
      (!amFilter || (a.amId || UNASSIGNED) === amFilter) &&
      (!q || `${a.name} ${a.clinicName} ${a.email}`.toLowerCase().includes(q))
    );
    rows.sort((a, b) => {
      // Accounts that never paid sort last on recency regardless of direction.
      const av = a[sortKey] ?? Number.POSITIVE_INFINITY;
      const bv = b[sortKey] ?? Number.POSITIVE_INFINITY;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [accounts, statusFilter, amFilter, search, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetPage = () => setPage(1);

  const toggleStatus = (s: Status) => {
    setStatusFilter(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
    resetPage();
  };

  const focusStatuses = (statuses: Status[]) => {
    setStatusFilter(new Set(statuses));
    resetPage();
  };

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      // Biggest loss first for change; biggest numbers first otherwise.
      setSortDir(key === 'change' ? 'asc' : 'desc');
    }
    resetPage();
  };

  const selectAm = (amId: string | null) => {
    setAmFilter(amId || UNASSIGNED);
    resetPage();
    accountsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const exportAccounts = () => {
    downloadCsv(
      `account-health-${mode === 'calendar' ? month : 'rolling-30d'}.csv`,
      ['Account', 'Clinic', 'Email', 'Account Manager', 'Status', 'Baseline / month', 'Current', 'Change', 'Change %', 'Last paid', 'Days since last paid', 'Lifetime revenue'],
      filtered.map(a => [
        a.name,
        a.clinicName,
        a.email,
        a.amName || 'Unassigned',
        STATUS_META[a.status].label,
        a.baselineMonthly.toFixed(2),
        a.current.toFixed(2),
        a.change.toFixed(2),
        a.changePct === null ? '' : (a.changePct * 100).toFixed(1),
        a.lastPaidAt ? new Date(a.lastPaidAt).toLocaleDateString('en-US') : '',
        a.daysSinceLastPaid ?? '',
        a.lifetimeRevenue.toFixed(2)
      ])
    );
  };

  const summary = data?.summary;
  const tile = (statuses: Status[]) => {
    const count = statuses.reduce((n, s) => n + (summary?.[s]?.accounts || 0), 0);
    const change = statuses.reduce((n, s) => n + (summary?.[s]?.change || 0), 0);
    return { count, change };
  };
  const slipping = tile(['slipping']);
  const churned = tile(['churned']);
  const growing = tile(['growing']);
  const added = tile(['new', 'reactivated']);
  const sameSet = (statuses: Status[]) =>
    statuses.length === statusFilter.size && statuses.every(s => statusFilter.has(s));

  const SortHeader: React.FC<{ k: SortKey; label: string }> = ({ k, label }) => (
    <th
      className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-800 whitespace-nowrap"
      onClick={() => toggleSort(k)}
      aria-sort={sortKey === k ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && <span className="text-gray-400">{sortDir === 'desc' ? '▼' : '▲'}</span>}
      </span>
    </th>
  );

  const visibleAms = showAllAms ? amRollup : amRollup.slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-rose-500" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Account Health</h3>
            <p className="text-xs text-gray-500">
              {data
                ? `${data.windows.label} vs ${mode === 'rolling' ? 'avg month of prior 90 days' : data.windows.baselineLabel} · paid invoices by payment date`
                : 'Paid invoices by payment date'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="Comparison">
            {([['rolling', 'Last 30 days'], ['calendar', 'Calendar month']] as [Mode, string][]).map(([m, label]) => (
              <button
                key={m}
                onClick={() => { setMode(m); resetPage(); }}
                aria-pressed={mode === m}
                className={`px-3 py-1.5 text-sm transition-colors ${mode === m ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {mode === 'calendar' && (
            <select
              value={month}
              onChange={e => { setMonth(e.target.value); resetPage(); }}
              aria-label="Month"
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {months.map(m => <option key={m.value} value={m.value}>{m.label} vs prior month</option>)}
            </select>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-600">
            Flag changes of
            <select
              value={threshold}
              onChange={e => { setThreshold(Number(e.target.value)); resetPage(); }}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[20, 30, 40, 50].map(t => <option key={t} value={t}>{t}%+</option>)}
            </select>
          </label>
        </div>
      </div>

      {query.isError ? (
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6 text-sm text-red-600">
          {(query.error as Error)?.message || 'Failed to load account health'}
        </div>
      ) : (
        <>
          {/* Summary tiles — click to filter the accounts table */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatTile
              label="Existing-account retention"
              icon={HeartPulse}
              value={query.isLoading ? '—' : data?.existingRetention == null ? 'n/a' : fmtPct(data.existingRetention)}
              sub="Current spend ÷ baseline, accounts with a baseline"
            />
            <StatTile
              label="Churned"
              icon={UserX}
              value={query.isLoading ? '—' : String(churned.count)}
              sub={query.isLoading ? undefined : `${fmtMoney(churned.change)} / month`}
              active={sameSet(['churned'])}
              onClick={() => focusStatuses(['churned'])}
            />
            <StatTile
              label="Slipping"
              icon={TrendingDown}
              value={query.isLoading ? '—' : String(slipping.count)}
              sub={query.isLoading ? undefined : `${fmtMoney(slipping.change)} / month`}
              active={sameSet(['slipping'])}
              onClick={() => focusStatuses(['slipping'])}
            />
            <StatTile
              label="Growing"
              icon={TrendingUp}
              value={query.isLoading ? '—' : String(growing.count)}
              sub={query.isLoading ? undefined : `+${fmtMoney(growing.change)} / month`}
              active={sameSet(['growing'])}
              onClick={() => focusStatuses(['growing'])}
            />
            <StatTile
              label="New + reactivated"
              icon={UserPlus}
              value={query.isLoading ? '—' : String(added.count)}
              sub={query.isLoading ? undefined : `+${fmtMoney(added.change)}`}
              active={sameSet(['new', 'reactivated'])}
              onClick={() => focusStatuses(['new', 'reactivated'])}
            />
          </div>

          {/* AM rollup */}
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Slippage by Account Manager</h3>
              <p className="text-xs text-gray-500">Sorted by revenue lost to slipping and churned accounts. Click a row to see its accounts.</p>
            </div>
            {query.isLoading ? (
              <div className="p-6 space-y-2">
                {[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}
              </div>
            ) : amRollup.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">No paid revenue in these windows.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <th className="px-3 py-2 text-left">Account Manager</th>
                        <th className="px-3 py-2 text-right">Accounts</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">Baseline / mo</th>
                        <th className="px-3 py-2 text-right">Current</th>
                        <th className="px-3 py-2 text-right">Net change</th>
                        <th className="px-3 py-2 text-right">Lost</th>
                        <th className="px-3 py-2 text-right">Gained</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">Slipping / churned</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visibleAms.map(r => {
                        const selected = amFilter === (r.amId || UNASSIGNED);
                        return (
                          <tr
                            key={r.amId || UNASSIGNED}
                            onClick={() => selectAm(r.amId)}
                            className={`cursor-pointer text-sm ${selected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                          >
                            <td className="px-3 py-2 font-medium text-gray-900">{r.amName}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{r.accounts}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{fmtMoney(r.baselineMonthly)}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{fmtMoney(r.current)}</td>
                            <td className={`px-3 py-2 text-right font-medium ${r.change < 0 ? 'text-red-600' : 'text-green-700'}`}>
                              {r.change < 0 ? '−' : '+'}{fmtMoney(Math.abs(r.change))}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-900">{r.lostRevenue < 0 ? `−${fmtMoney(-r.lostRevenue)}` : '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-900">{r.gainedRevenue > 0 ? `+${fmtMoney(r.gainedRevenue)}` : '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-700">{r.slippingAccounts} / {r.churnedAccounts}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {amRollup.length > 10 && (
                  <button
                    onClick={() => setShowAllAms(s => !s)}
                    className="w-full px-4 py-2 text-sm text-blue-600 hover:bg-gray-50 border-t border-gray-100"
                  >
                    {showAllAms ? 'Show top 10' : `Show all ${amRollup.length} account managers`}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Accounts */}
          <div ref={accountsRef} className="bg-white shadow-sm rounded-2xl border border-gray-200 overflow-hidden scroll-mt-4">
            <div className="px-4 py-3 border-b border-gray-100 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Accounts</h3>
                  <p className="text-xs text-gray-500">
                    {query.isLoading ? 'Loading…' : `${filtered.length} of ${accounts.length} accounts with paid revenue in either window`}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      value={search}
                      onChange={e => { setSearch(e.target.value); resetPage(); }}
                      placeholder="Search account or clinic"
                      className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <select
                    value={amFilter}
                    onChange={e => { setAmFilter(e.target.value); resetPage(); }}
                    aria-label="Account manager"
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All account managers</option>
                    {amOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button
                    onClick={exportAccounts}
                    disabled={filtered.length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    CSV
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {STATUS_ORDER.map(s => {
                  const on = statusFilter.has(s);
                  const Icon = STATUS_META[s].icon;
                  return (
                    <button
                      key={s}
                      onClick={() => toggleStatus(s)}
                      aria-pressed={on}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                        on ? STATUS_META[s].className : 'bg-white text-gray-400 border-gray-200 hover:text-gray-600'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {STATUS_META[s].label}
                      <span className="opacity-70">{summary?.[s]?.accounts ?? 0}</span>
                    </button>
                  );
                })}
                {amFilter && (
                  <button
                    onClick={() => { setAmFilter(''); resetPage(); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-xs text-gray-700 hover:bg-gray-200"
                  >
                    {amOptions.find(o => o.value === amFilter)?.label || 'Account manager'}
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {query.isLoading ? (
              <div className="p-6 space-y-2">
                {[...Array(6)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">No accounts match these filters.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Account</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">AM</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">6-mo trend</th>
                        <SortHeader k="baselineMonthly" label="Baseline / mo" />
                        <SortHeader k="current" label="Current" />
                        <SortHeader k="change" label="Change" />
                        <SortHeader k="daysSinceLastPaid" label="Last paid" />
                        <SortHeader k="lifetimeRevenue" label="Lifetime" />
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pageRows.map(a => (
                        <tr key={a.physicianId} className="text-sm hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <Link
                              to={`/users?q=${encodeURIComponent(a.email)}`}
                              className="font-medium text-gray-900 hover:text-blue-600"
                            >
                              {a.clinicName || a.name}
                            </Link>
                            {a.clinicName && <div className="text-xs text-gray-500">{a.name}</div>}
                          </td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{a.amName || <span className="text-gray-400">Unassigned</span>}</td>
                          <td className="px-3 py-2"><Sparkline points={a.sparkline} /></td>
                          <td className="px-3 py-2 text-right text-gray-700">{fmtMoney(a.baselineMonthly)}</td>
                          <td className="px-3 py-2 text-right text-gray-700">{fmtMoney(a.current)}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <div className={`font-medium ${a.change < 0 ? 'text-red-600' : 'text-green-700'}`}>
                              {a.change < 0 ? '−' : '+'}{fmtMoney(Math.abs(a.change))}
                            </div>
                            {a.changePct !== null && (
                              <div className="text-xs text-gray-500">{a.changePct > 0 ? '+' : ''}{fmtPct(a.changePct)}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                            {a.daysSinceLastPaid === null ? '—' : `${a.daysSinceLastPaid}d ago`}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700">{fmtMoney(a.lifetimeRevenue)}</td>
                          <td className="px-3 py-2"><StatusPill status={a.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {pageCount > 1 && (
                  <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
                    <span>Page {currentPage} of {pageCount}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                        disabled={currentPage === pageCount}
                        className="px-3 py-1 border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="px-4 py-2 border-t border-gray-100 flex items-start gap-2 text-xs text-gray-500">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                {mode === 'rolling'
                  ? 'Baseline is the average month of the 90 days before the last 30.'
                  : 'Baseline is the prior calendar month (Eastern time).'}
                {' '}Slipping and growing use the {threshold}% threshold. AM is the account's current assignment.
              </span>
            </div>
          </div>
        </>
      )}

      <RetentionSection />
      <ActivationSection amFilter={amFilter} amOptions={amOptions} onAmFilterChange={setAmFilter} />
      <RewardsSection />
    </div>
  );
};

export default AccountHealthPanel;
